import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";
import OutsideClickHandler from "react-outside-click-handler";
import { previewSegment } from "../../../api/campaigns.service";
import { getUsers } from "../../../api/user.service";
import { CHANNELS, fmtDate } from "../shared";
import styles from "./Audience.module.sass";

const LEVELS = ["Newbie", "Beginner", "Intermediate", "Advanced", "Master"];
const LANGS = ["es", "en", "fr", "pt", "ru"];

/** Puntos de partida de un clic. */
const PRESETS = [
  { emoji: "🌍", label: "Todos", def: {} },
  { emoji: "🔥", label: "Activos", def: { activeInLastDays: 30 } },
  { emoji: "💤", label: "Dormidos", def: { inactiveForDays: 90 } },
  { emoji: "🌱", label: "Nuevos", def: { registeredInLastDays: 7 } },
  { emoji: "👻", label: "Curiosos", def: { neverPlayed: true, seenInLastDays: 14 } },
  { emoji: "💎", label: "Premium", def: { premium: true } },
];

/** Catálogo del «＋ Añadir filtro», con descripción. */
const FILTERS = [
  { key: "game", emoji: "🎮", label: "Actividad de juego", desc: "Jugaron hace poco, hace mucho o nunca" },
  { key: "visit", emoji: "👀", label: "Última visita", desc: "Cuándo abrieron la app por última vez" },
  { key: "signup", emoji: "📅", label: "Antigüedad", desc: "Registrados en los últimos días" },
  { key: "plan", emoji: "💎", label: "Plan", desc: "Solo premium o solo free" },
  { key: "level", emoji: "🎓", label: "Nivel", desc: "Nivel de chino del onboarding" },
  { key: "lang", emoji: "🌐", label: "Idioma", desc: "Idioma de la app" },
  { key: "email", emoji: "✉️", label: "Email confirmado", desc: "Imprescindible para campañas de mail" },
  { key: "users", emoji: "🎯", label: "Usuarios concretos", desc: "Elegir personas a mano por nombre o email" },
];

const cleanDefinition = (d) => {
  const out = {};
  Object.entries(d || {}).forEach(([k, v]) => {
    if (v === "" || v === undefined || v === null) return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
};

const initials = (s = "") =>
  String(s)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("") || "?";

const displayName = (u) => u.name || u.username || u.email || "—";
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

export const audienceSentence = (def) => {
  if (def.userIds?.length) {
    return `Envío dirigido: ${def.userIds.length} usuario${def.userIds.length > 1 ? "s" : ""} elegido${
      def.userIds.length > 1 ? "s" : ""
    } a mano.`;
  }
  const parts = [];
  if (def.activeInLastDays) parts.push(`jugaron en los últimos ${def.activeInLastDays} días`);
  if (def.inactiveForDays) parts.push(`llevan ${def.inactiveForDays}+ días sin jugar`);
  if (def.neverPlayed) parts.push("nunca han jugado");
  if (def.seenInLastDays) parts.push(`abrieron la app en los últimos ${def.seenInLastDays} días`);
  if (def.notSeenForDays) parts.push(`no abren la app desde hace ${def.notSeenForDays}+ días`);
  if (def.registeredInLastDays) parts.push(`se registraron hace ≤ ${def.registeredInLastDays} días`);
  if (def.premium === true) parts.push("son premium");
  if (def.premium === false) parts.push("son free");
  if (def.levels?.length) parts.push(`nivel ${def.levels.join("/")}`);
  if (def.langs?.length) parts.push(`app en ${def.langs.map((l) => l.toUpperCase()).join("·")}`);
  if (def.confirmedMailOnly) parts.push("tienen el email confirmado");
  let sentence = parts.length ? `Usuarios que ${parts.join(", ")}` : "Todos los usuarios";
  if (def.limit) {
    const orderLabel =
      { lastPlayed: "última partida", lastAccess: "última visita", signupDate: "alta" }[
        def.orderBy?.field
      ] || "última partida";
    sentence += ` — los ${fmtNum(def.limit)} más recientes por ${orderLabel}`;
  }
  if (def.excludeContactedInLastDays)
    sentence += `, sin repetir a nadie contactado en los últimos ${def.excludeContactedInLastDays} días`;
  return sentence + ".";
};

const matchesPreset = (def, preset) => {
  const KEYS = [
    "activeInLastDays",
    "inactiveForDays",
    "neverPlayed",
    "seenInLastDays",
    "notSeenForDays",
    "registeredInLastDays",
    "premium",
  ];
  if (def.userIds?.length) return false;
  return KEYS.every((k) => (def[k] ?? null) === (preset.def[k] ?? null));
};

const activeFilters = (def) => {
  const active = [];
  if (def.activeInLastDays || def.inactiveForDays || def.neverPlayed) active.push("game");
  if (def.seenInLastDays || def.notSeenForDays) active.push("visit");
  if (def.registeredInLastDays) active.push("signup");
  if (def.premium !== undefined) active.push("plan");
  if (def.levels?.length) active.push("level");
  if (def.langs?.length) active.push("lang");
  if (def.confirmedMailOnly) active.push("email");
  if (def.userIds?.length || def.userIds) active.push("users");
  return active;
};

/**
 * AUDIENCIA Y ENVÍO — v6, guiada en tres tiempos:
 *
 *   HERO (cuántos son y por qué) → «Empieza por» (presets) → «Afina» (chips
 *   combinables + a dedo) → ENVÍO (dos modos como tarjetas, cuántos, y un
 *   interruptor de «No repetir» con su impacto en vivo).
 */
const Audience = ({
  value,
  onChange,
  disabled,
  channel,
  channels = [],
  batchSize,
  onBatchSize,
  onTargeted,
}) => {
  const def = value || {};
  const [preview, setPreview] = useState(null);
  const [totalUsers, setTotalUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const debounceRef = useRef(null);

  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [pickedUsers, setPickedUsers] = useState({});
  const userSearchRef = useRef(null);

  useEffect(() => {
    previewSegment({ sampleSize: 1 })
      .then((resp) => setTotalUsers(resp?.data?.total ?? null))
      .catch(() => {});
  }, []);

  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const set = (patch) => !disabled && onChange({ ...def, ...patch });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await previewSegment(cleanDefinition(def));
        if (resp?.data && typeof resp.data.total === "number") {
          setPreview(resp.data);
          onTargeted?.(resp.data.targeted);
        }
      } catch (e) {
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(def)]);

  useEffect(() => {
    if (userSearchRef.current) clearTimeout(userSearchRef.current);
    if (!userQuery.trim()) {
      setUserResults([]);
      return undefined;
    }
    userSearchRef.current = setTimeout(async () => {
      try {
        const resp = await getUsers({ search: userQuery.trim(), take: 6 });
        const list = resp?.data?.results || resp?.data || [];
        setUserResults(Array.isArray(list) ? list.slice(0, 6) : []);
      } catch (e) {
        setUserResults([]);
      }
    }, 350);
    return () => userSearchRef.current && clearTimeout(userSearchRef.current);
  }, [userQuery]);

  const openRecipients = async () => {
    setRecipientsOpen(true);
    setRecipientsLoading(true);
    try {
      const resp = await previewSegment({ ...cleanDefinition(def), sampleSize: 50 });
      setRecipients(resp?.data || null);
    } catch (e) {
      setRecipients(null);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const toggleArray = (key, item) => {
    const arr = def[key] || [];
    set({ [key]: arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item] });
  };

  const addUser = (u) => {
    const id = u._id;
    if (!id || (def.userIds || []).includes(id)) return;
    setPickedUsers((prev) => ({ ...prev, [id]: { name: displayName(u), email: u.email } }));
    set({ userIds: [...(def.userIds || []), id] });
    setUserQuery("");
    setUserResults([]);
  };
  const removeUser = (id) => {
    const next = (def.userIds || []).filter((x) => x !== id);
    set({ userIds: next.length ? next : undefined });
  };

  const addFilter = (key) => {
    setAddOpen(false);
    if (key === "game") set({ inactiveForDays: 90 });
    if (key === "visit") set({ seenInLastDays: 14 });
    if (key === "signup") set({ registeredInLastDays: 7 });
    if (key === "plan") set({ premium: true });
    if (key === "level") set({ levels: [LEVELS[0]] });
    if (key === "lang") set({ langs: ["es"] });
    if (key === "email") set({ confirmedMailOnly: true });
    if (key === "users") set({ userIds: def.userIds ?? [] });
  };
  const removeFilter = (key) => {
    if (key === "game")
      set({ activeInLastDays: undefined, inactiveForDays: undefined, neverPlayed: undefined });
    if (key === "visit") set({ seenInLastDays: undefined, notSeenForDays: undefined });
    if (key === "signup") set({ registeredInLastDays: undefined });
    if (key === "plan") set({ premium: undefined });
    if (key === "level") set({ levels: undefined });
    if (key === "lang") set({ langs: undefined });
    if (key === "email") set({ confirmedMailOnly: undefined });
    if (key === "users") set({ userIds: undefined });
  };

  const sample = preview?.sample || [];
  const active = activeFilters(def);
  const available = FILTERS.filter((f) => !active.includes(f.key));
  const noRepeatOn = (def.excludeContactedInLastDays ?? 0) > 0;
  const usesBatches = (batchSize ?? 0) > 0;
  const pct =
    totalUsers && preview ? Math.min(100, Math.round((preview.targeted / totalUsers) * 100)) : 0;
  const days =
    usesBatches && preview?.targeted ? Math.max(1, Math.ceil(preview.targeted / batchSize)) : null;
  const perBatch = usesBatches ? batchSize : preview?.targeted;

  const gameMode = def.activeInLastDays ? "active" : def.neverPlayed ? "never" : "inactive";
  const visitMode = def.notSeenForDays ? "unseen" : "seen";

  const daysInput = (key, fallback) => (
    <input
      className={styles.numInput}
      type="number"
      min="1"
      disabled={disabled}
      value={def[key] ?? fallback}
      onChange={(e) => set({ [key]: Math.max(1, Number(e.target.value) || fallback) })}
    />
  );

  const select = (value_, onChangeValue, options) => (
    <select
      className={styles.select}
      disabled={disabled}
      value={value_}
      onChange={(e) => onChangeValue(e.target.value)}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );

  const filterChip = (key, emoji, title, content) => (
    <div key={key} className={styles.chip}>
      <div className={styles.chipHead}>
        <span className={styles.chipEmoji}>{emoji}</span>
        <span className={styles.chipTitle}>{title}</span>
        {!disabled && (
          <button
            type="button"
            className={styles.chipClose}
            title="Quitar filtro"
            onClick={() => removeFilter(key)}
          >
            ✕
          </button>
        )}
      </div>
      <div className={styles.chipBody}>{content}</div>
    </div>
  );

  return (
    <div className={cn(styles.audience, { [styles.disabled]: disabled })}>
      {/* ══ HERO ══ */}
      <div className={styles.hero}>
        <div className={cn(styles.ring, { [styles.ringLoading]: loading })} style={{ "--pct": `${pct}%` }}>
          <div className={styles.ringInner}>
            <span className={styles.ringPct}>{`${pct}%`}</span>
            <span className={styles.ringOf}>de la base</span>
          </div>
        </div>
        <div className={styles.heroMain}>
          <div className={styles.heroCount}>
            <span className={styles.heroNumber}>
              {loading ? "…" : preview ? fmtNum(preview.targeted) : "—"}
            </span>
            <span className={styles.heroCountLabel}>destinatarios</span>
          </div>
          <div className={styles.heroSentence}>{audienceSentence(def)}</div>
          <div className={styles.heroFacts}>
            {preview && preview.targeted < preview.total && (
              <span className={styles.fact}>de {fmtNum(preview.total)} que cumplen</span>
            )}
            {preview?.excludedByNoRepeat > 0 && (
              <span className={cn(styles.fact, styles.factHot)}>
                −{fmtNum(preview.excludedByNoRepeat)} por no repetir
              </span>
            )}
          </div>
        </div>
        <div className={styles.heroSide}>
          <div className={styles.sampleAvatars}>
            {sample.slice(0, 4).map((u, i) => (
              <div key={u._id || i} className={styles.sampleAvatar} title={u.email || ""}>
                {initials(displayName(u))}
              </div>
            ))}
          </div>
          <button type="button" className={styles.viewRecipients} onClick={openRecipients}>
            Ver destinatarios
            <Icon name="arrow-right" size="14" />
          </button>
        </div>
      </div>

      {/* ══ EMPIEZA POR ══ */}
      {!disabled && (
        <div className={styles.guideRow}>
          <span className={styles.guideLabel}>Empieza por</span>
          <div className={styles.presets}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={cn(styles.preset, { [styles.presetActive]: matchesPreset(def, p) })}
                onClick={() =>
                  onChange({
                    ...p.def,
                    limit: def.limit,
                    orderBy: def.orderBy,
                    excludeContactedInLastDays: def.excludeContactedInLastDays,
                  })
                }
              >
                <span className={styles.presetEmoji}>{p.emoji}</span>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══ AFINA ══ */}
      <div className={styles.guideRow}>
        <span className={styles.guideLabel}>Afina</span>
        <div className={styles.chips}>
          {active.includes("game") &&
            filterChip(
              "game",
              "🎮",
              "Juego",
              <>
                {select(gameMode, (m) =>
                  set({
                    activeInLastDays: m === "active" ? 30 : undefined,
                    inactiveForDays: m === "inactive" ? 90 : undefined,
                    neverPlayed: m === "never" ? true : undefined,
                  }),
                  [
                    ["active", "Jugaron hace ≤"],
                    ["inactive", "Sin jugar desde hace ≥"],
                    ["never", "Nunca jugaron"],
                  ]
                )}
                {gameMode === "active" && daysInput("activeInLastDays", 30)}
                {gameMode === "inactive" && daysInput("inactiveForDays", 90)}
                {gameMode !== "never" && <span className={styles.unit}>días</span>}
              </>
            )}

          {active.includes("visit") &&
            filterChip(
              "visit",
              "👀",
              "Visita",
              <>
                {select(visitMode, (m) =>
                  set({
                    seenInLastDays: m === "seen" ? 14 : undefined,
                    notSeenForDays: m === "unseen" ? 60 : undefined,
                  }),
                  [
                    ["seen", "Abrieron la app hace ≤"],
                    ["unseen", "Sin abrir desde hace ≥"],
                  ]
                )}
                {visitMode === "seen" ? daysInput("seenInLastDays", 14) : daysInput("notSeenForDays", 60)}
                <span className={styles.unit}>días</span>
              </>
            )}

          {active.includes("signup") &&
            filterChip(
              "signup",
              "📅",
              "Alta",
              <>
                <span className={styles.unit}>hace ≤</span>
                {daysInput("registeredInLastDays", 7)}
                <span className={styles.unit}>días</span>
              </>
            )}

          {active.includes("plan") &&
            filterChip(
              "plan",
              "💎",
              "Plan",
              select(def.premium === true ? "premium" : "free", (v) => set({ premium: v === "premium" }), [
                ["premium", "Solo premium"],
                ["free", "Solo free"],
              ])
            )}

          {active.includes("level") &&
            filterChip(
              "level",
              "🎓",
              "Nivel",
              <div className={styles.toggles}>
                {LEVELS.map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    disabled={disabled}
                    className={cn(styles.toggle, {
                      [styles.toggleActive]: (def.levels || []).includes(lvl),
                    })}
                    onClick={() => toggleArray("levels", lvl)}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            )}

          {active.includes("lang") &&
            filterChip(
              "lang",
              "🌐",
              "Idioma",
              <div className={styles.toggles}>
                {LANGS.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    disabled={disabled}
                    className={cn(styles.toggle, {
                      [styles.toggleActive]: (def.langs || []).includes(lang),
                    })}
                    onClick={() => toggleArray("langs", lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

          {active.includes("email") &&
            filterChip("email", "✉️", "Email", <span className={styles.unit}>Solo confirmados</span>)}

          {active.includes("users") &&
            filterChip(
              "users",
              "🎯",
              "A dedo",
              <div className={styles.userPicker}>
                {(def.userIds || []).length > 0 && (
                  <div className={styles.userChips}>
                    {(def.userIds || []).map((id) => (
                      <span key={id} className={styles.userChip}>
                        {pickedUsers[id]?.name || `…${id.slice(-5)}`}
                        {!disabled && (
                          <button type="button" onClick={() => removeUser(id)}>
                            ✕
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
                {!disabled && (
                  <div className={styles.userSearch}>
                    <Icon name="search" size="14" />
                    <input
                      placeholder="Nombre o email…"
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                    />
                    {userResults.length > 0 && (
                      <div className={styles.userResults}>
                        {userResults.map((u) => (
                          <button key={u._id} type="button" onClick={() => addUser(u)}>
                            <span className={styles.sampleAvatar}>{initials(displayName(u))}</span>
                            <span className={styles.userResultName}>{displayName(u)}</span>
                            <span className={styles.userResultEmail}>{u.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          {!disabled && available.length > 0 && (
            <OutsideClickHandler onOutsideClick={() => setAddOpen(false)}>
              <div className={styles.addFilter}>
                <button
                  type="button"
                  className={cn(styles.addFilterButton, { [styles.addFilterOpen]: addOpen })}
                  onClick={() => setAddOpen((v) => !v)}
                >
                  ＋ Añadir filtro
                </button>
                {addOpen && (
                  <div className={styles.addMenu}>
                    {available.map((f) => (
                      <button key={f.key} type="button" onClick={() => addFilter(f.key)}>
                        <span className={styles.addMenuEmoji}>{f.emoji}</span>
                        <span className={styles.addMenuBody}>
                          <span className={styles.addMenuLabel}>{f.label}</span>
                          <span className={styles.addMenuDesc}>{f.desc}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </OutsideClickHandler>
          )}
        </div>
      </div>

      {/* ══ ENVÍO ══ */}
      <div className={styles.sending}>
        <div className={styles.sendingHead}>
          <span className={styles.sendingTitle}>Envío</span>
          {perBatch > 0 && channels.length > 0 && (
            <span className={styles.perChannel}>
              {channels.map((ch) => {
                const meta = CHANNELS[ch] || {};
                return (
                  <span key={ch} className={styles.perChannelPill} style={{ "--ch": meta.color }}>
                    <Icon name={meta.icon || "notification"} size="13" />
                    {fmtNum(perBatch)}
                  </span>
                );
              })}
              <span className={styles.perChannelLabel}>
                {usesBatches ? "por tanda y canal" : "por canal"}
              </span>
            </span>
          )}
        </div>

        {/* modo: golpe / tandas */}
        <div className={styles.modes}>
          <button
            type="button"
            disabled={disabled}
            className={cn(styles.mode, { [styles.modeActive]: !usesBatches })}
            onClick={() => onBatchSize?.(undefined)}
          >
            <span className={styles.modeEmoji}>🚀</span>
            <span className={styles.modeBody}>
              <span className={styles.modeName}>Todo de golpe</span>
              <span className={styles.modeDesc}>Una sola tanda al confirmar</span>
            </span>
          </button>
          <button
            type="button"
            disabled={disabled}
            className={cn(styles.mode, { [styles.modeActive]: usesBatches })}
            onClick={() => !usesBatches && onBatchSize?.(15000)}
          >
            <span className={styles.modeEmoji}>📅</span>
            <span className={styles.modeBody}>
              <span className={styles.modeName}>
                En tandas de{" "}
                {usesBatches ? (
                  <input
                    className={styles.numInput}
                    type="number"
                    min="100"
                    step="500"
                    disabled={disabled}
                    value={batchSize}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onBatchSize?.(Math.max(1, Number(e.target.value) || 0) || undefined)}
                  />
                ) : (
                  "15.000"
                )}{" "}
                al día
              </span>
              <span className={styles.modeDesc}>
                {usesBatches && days != null
                  ? `≈ ${days} ${days === 1 ? "día" : "días"} · cron a las 10:00 o a mano`
                  : "El cron las suelta cada mañana"}
              </span>
            </span>
          </button>
        </div>

        {/* timeline de tandas */}
        {usesBatches && days != null && days > 1 && preview?.targeted && (
          <div className={styles.timeline}>
            {Array.from({ length: Math.min(days, 8) }, (_, i) => {
              const isOverflow = days > 8 && i === 7;
              const size = i < days - 1 ? batchSize : preview.targeted - batchSize * (days - 1);
              return (
                <div key={i} className={styles.timelineItem}>
                  <span className={styles.timelineDay}>{i === 0 ? "al enviar" : `día ${i + 1}`}</span>
                  <span className={styles.timelineSize}>{isOverflow ? "…" : fmtNum(size)}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* cuántos */}
        <div className={styles.sendRow}>
          <span className={styles.sendLabel}>Cuántos</span>
          {select(def.limit ? "top" : "all", (v) =>
            set({
              limit: v === "top" ? def.limit ?? 300 : undefined,
              orderBy:
                v === "top" ? def.orderBy ?? { field: "lastPlayed", direction: "desc" } : def.orderBy,
            }),
            [
              ["all", "Todos los que cumplan"],
              ["top", "Solo los N más recientes"],
            ]
          )}
          {def.limit ? (
            <>
              <input
                className={styles.numInput}
                type="number"
                min="1"
                disabled={disabled}
                value={def.limit}
                onChange={(e) => set({ limit: Math.max(1, Number(e.target.value) || 300) })}
              />
              {select(def.orderBy?.field ?? "lastPlayed", (v) =>
                set({ orderBy: { field: v, direction: "desc" } }),
                [
                  ["lastPlayed", "por última partida"],
                  ["lastAccess", "por última visita"],
                  ["signupDate", "por fecha de alta"],
                ]
              )}
            </>
          ) : null}
        </div>

        {/* no repetir: interruptor */}
        <div className={styles.noRepeat}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              disabled={disabled}
              checked={noRepeatOn}
              onChange={(e) => set({ excludeContactedInLastDays: e.target.checked ? 30 : undefined })}
            />
            <span className={styles.slider} />
          </label>
          <div className={styles.noRepeatBody}>
            <span className={styles.noRepeatTitle}>No repetir</span>
            <span className={styles.noRepeatDesc}>
              {noRepeatOn ? (
                <>
                  Excluye a quien ya recibió algo en los últimos{" "}
                  {daysInput("excludeContactedInLastDays", 30)} días
                </>
              ) : (
                "Da igual si ya recibieron otra campaña"
              )}
            </span>
          </div>
          {noRepeatOn && preview?.excludedByNoRepeat != null && (
            <span className={styles.impact}>−{fmtNum(preview.excludedByNoRepeat)}</span>
          )}
        </div>
      </div>

      {/* ══ MODAL DESTINATARIOS ══ */}
      <Modal
        visible={recipientsOpen}
        onClose={() => setRecipientsOpen(false)}
        outerClassName={styles.recipientsModal}
      >
        <div className={styles.recipients}>
          <div className={cn("title-purple", styles.recipientsTitle)}>
            Destinatarios
            {recipients ? (
              <span className={styles.recipientsCount}>
                ≈ {fmtNum(recipients.targeted)} en total · primeros{" "}
                {Math.min(recipients.sample?.length || 0, 50)} según el orden del envío
              </span>
            ) : null}
          </div>
          <div className={styles.recipientsSentence}>{audienceSentence(def)}</div>
          {recipientsLoading ? (
            <div className={styles.recipientsLoading}>Cargando…</div>
          ) : !recipients?.sample?.length ? (
            <div className={styles.recipientsLoading}>Sin resultados con estos filtros.</div>
          ) : (
            <div className={styles.recipientsTable}>
              <div className={cn(styles.recipientRow, styles.recipientHead)}>
                <div>Usuario</div>
                <div>Nivel</div>
                <div>Última partida</div>
                <div>Ya contactado</div>
              </div>
              {recipients.sample.map((u, i) => (
                <div className={styles.recipientRow} key={u._id || i}>
                  <div className={styles.recipientUser}>
                    <div className={styles.sampleAvatar}>{initials(displayName(u))}</div>
                    <div className={styles.recipientId}>
                      <div className={styles.recipientName}>
                        {displayName(u)}
                        {u.premium && <span className={styles.premiumBadge}>💎</span>}
                      </div>
                      {u.email && <div className={styles.recipientEmail}>{u.email}</div>}
                    </div>
                  </div>
                  <div>{u.level || "—"}</div>
                  <div>{u.lastPlayed ? fmtDate(u.lastPlayed) : "nunca"}</div>
                  <div>
                    {u.lastContactedAt ? (
                      <span className={styles.contactedBadge}>📬 {fmtDate(u.lastContactedAt)}</span>
                    ) : (
                      <span className={styles.freshBadge}>✨ nunca</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Audience;
